import { ComponentRef, Injector, NgZone, TemplateRef, Type } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ApiService, LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { Environment, ISettings } from '@lsq-frontend/angular-shared/settings';
import {
    BaseFilter,
    BaseListFilter,
    BaseRangeFilter,
    Guid,
    Request,
    User,
    UserCompany
} from '@lsq-frontend/remote-services/enterprise/common';
import { Observable, Subject, Subscription } from 'rxjs';
import { DatePickerType } from '../components/ui-elements/date-picker/constants/date-picker-type';
import { ClassNames } from '../constants/class-names.constants';
import { Color } from '../constants/color.constants';
import { ModalConstants } from '../constants/modal.constants';
import { Orientation } from '../constants/orientation.constants';
import { DayOfWeek } from './enums';

export interface ISha {
    status: string;
    version: string;
    sha: string;
    date: string;
}

export interface EndpointProperty {
    name: string;
    type: string;
    defaultValue: any;
    validators?: ValidatorFn[];
    options?: any;
    nullable?: boolean;
}

export interface IClaims {
    [key: string]: boolean;
}

export interface IAuthenticationResult {
    authorized: boolean;
    emailAddress?: string;
    requiresAuthenticationCode?: boolean;
    authenticationCodeExpired?: boolean;
    authenticationCodeIncorrect?: boolean;
    authenticationCodeInvalid?: boolean;
    message?: string;
    accessToken?: string;
    idToken?: string;
    refreshToken?: string;
    expiresInMillis?: number;
    phoneNumberLast4?: string;
    requiresPasswordReset?: boolean;
    mfaType?: MFAType;
}

export enum MFAType {
    SMS = 1,
    TOTP = 2
}
export enum AuthenticationStep {
    Login = 1,
    ForgotPassword = 10,
    UserResetPassword = 11,
    ForceResetPassword = 12,
    NewPasswordRequired = 13,
    BackupCodeRequired = 14
}

export interface IDropdownListItem {
    label: string;
    value?: any;
    isChild?: boolean;
    isGrandChild?: boolean;
    isGreatGrandChild?: boolean;
    children?: IDropdownListItem[];
}

export interface IFiltersObject {
    [key: string]: BaseFilter<any> | BaseListFilter<any> | BaseRangeFilter<any>;
}

export interface FeatureFlags {
    [key: string]: string | number | boolean;
}

export class DropdownType {
    public static readonly Normal = 'normal';
    public static readonly Checklist = 'checklist';
}

export interface ISelectOption<T = any> {
    label: string;
    subLabel?: string;
    selectedLabel?: string;
    value?: T;
    isChild?: boolean;
    titleText?: string;
    children?: ISelectOption<T>[];
}

export interface IChecklistItem extends IDropdownListItem {
    disabled?: boolean;
    partiallyChecked?: boolean;
}

export interface IRadioOption {
    id?: string;
    label?: string;
    value?: any;
    checked?: boolean;
}

export class RadioOption implements IRadioOption {
    public id: string;
    public label: string;
    public value: any;
    public checked: boolean;
    constructor(obj?: { id?: string; label?: string; value?: any; checked?: boolean }) {
        if (obj != null) {
            this.id = typeof obj.id === 'undefined' ? this.id : obj.id;
            this.label = typeof obj.label === 'undefined' ? this.label : obj.label;
            this.value = typeof obj.value === 'undefined' ? this.value : obj.value;
            this.checked = typeof obj.checked === 'undefined' ? this.checked : obj.checked;
        }
    }
}

export type RouteInterceptor = (
    activatedRouteSnapshot?: ActivatedRouteSnapshot,
    injector?: Injector,
    settings?: ISettings
) => Observable<boolean>;

export class NavigationItem implements IDropdownListItem {
    public children?: NavigationItem[];
    public linkClassName? = '';
    public icon?: string;
    public counter?: number;
    public isChild?: boolean;
    public isGrandChild?: boolean;
    public featureFlags?: string[];
    public claims?: number[];
    public environments?: Environment[];
    public allowAnonymous? = false;
    public label: string;
    public position? = 0;
    public modal?: Modal;
    public queryParams?: Object;
    public routerLink?: string[];
    public routerLinkUrl?: string;
    public routerLinkActive?: string;
    public routerLinkActiveOptions?: {
        exact: boolean;
    };
    public externalLink?: string;
    public hideInMobile?: boolean;
    public hideInDesktop?: boolean;
    public hideChildren? = false;
    public data?: { [key: string]: any };
    public showChildren? = false;

    constructor(item: Partial<NavigationItem>) {
        if (
            !item ||
            !item.label ||
            (item.routerLink == null && item.externalLink == null && item.modal == null)
        ) {
            throw new Error('Invalid configuration for NavigationItem');
        }
        this.children = item.children;
        this.linkClassName = item.linkClassName;
        this.icon = item.icon;
        this.counter = item.counter;
        this.isChild = item.isChild;
        this.isGrandChild = item.isGrandChild;
        this.featureFlags = item.featureFlags;
        this.claims = item.claims;
        this.environments = item.environments;
        this.allowAnonymous = item.allowAnonymous;
        this.label = item.label;
        this.position = item.position || 0;
        this.routerLink = item.routerLink;
        this.queryParams = item.queryParams;
        this.routerLinkUrl = item.routerLinkUrl;
        this.routerLinkActive = item.routerLinkActive || ClassNames.Active;
        this.routerLinkActiveOptions = item.routerLinkActiveOptions || {
            exact: false
        };
        this.externalLink = item.externalLink;
        this.hideInMobile = item.hideInMobile;
        this.hideInDesktop = item.hideInDesktop;
        this.hideChildren = item.hideChildren;
        this.data = item.data;
        this.modal = item.modal;
    }
}

export class DatePickerConfiguration {
    public type?: DatePickerType = DatePickerType.Single;
    public sideBySide?: boolean;
    public model?: any;
    public modelChange?: (event: any) => void;
    public calendarLabel?: string;
    public inputClass? = '';
    public parentClass? = '';
    public allowFuture? = false;
    public lowerLimit?: Date;
    public upperLimit?: Date;
}

export class DateOnly {
    public year: number;
    public dayOfWeek?: DayOfWeek;

    constructor(
        dateOrYear: Date | string | number = new Date(),
        public month = 1, // 1-based, not 0-based like JavaScript `Date#getMonth`
        public date = 1
    ) {
        if (dateOrYear instanceof Date) {
            this.applyDate(dateOrYear);
        } else if (typeof dateOrYear === 'string') {
            const theDate = new Date(dateOrYear);
            if (theDate) {
                this.applyDate(theDate);
            }
        } else if (typeof dateOrYear === 'number') {
            this.year = dateOrYear;
            this.month = month;
            this.date = date;
        } else {
            throw new Error(
                'Invalid argument for DateOnly constructor. Please provide a Date, a date string, or year, month, and date.'
            );
        }
    }

    private applyDate(date: Date): void {
        this.year = date.getFullYear();
        this.month = date.getMonth() + 1;
        this.date = date.getDate();
        this.dayOfWeek = date.getDay() as DayOfWeek;
    }

    public toString(): string {
        return `${this.year}/${this.month}/${this.date}`;
    }

    public getDayOfWeekString(): string {
        return DayOfWeek[this.dayOfWeek];
    }
}

export class NavigationState {
    public user: User = undefined;
    public companyName: String = undefined;
    public companies: UserCompany[] = [];
    public navigationItems: NavigationItem[] = [];
    public desktopSettingsDropdownNavigationItems: NavigationItem[] = [];
    public settingsDropdownNavigationItems: NavigationItem[] = [];
    public allNavigationItems: NavigationItem[] = [];
    public mobileNavigationIsExpanded = false;
    public menuIsExpanded = false;
    public isMobile = false;
}

export interface IPopoverOption {
    click?: boolean;
    content?: string;
    displayIcon?: boolean;
    icon?: string;
    hover?: boolean;
    orientation?: Orientation;
    parentClass?: string;
    template?: string;
    title?: string;
}

export class PopoverOption implements IPopoverOption {
    public click? = false;
    public content?: string;
    public displayIcon? = true;
    public hover? = true;
    public orientation?: Orientation;
    public parentClass?: string;
    public icon?: string;
    public template?: string;
    public title?: string;
    constructor(option: PopoverOption) {
        this.click = option.click || false;
        this.content = option.content;
        this.displayIcon = option.displayIcon || true;
        this.hover = option.hover || true;
        this.icon = option.icon;
        this.orientation = option.orientation || Orientation.Top;
        this.parentClass = option.parentClass;
        this.template = option.template;
        this.title = option.title;
    }
}

export interface ITemplatePointer {
    key: Symbol;
    template: TemplateRef<any>;
}

export class ActionSheet {
    public id?: string;
    public templateRef?: TemplateRef<object>;
    public context?: object;
    public height?: number;
    public stickToTop? = false;
    public showCloseButton? = true;
    public closeButtonColor? = Color.Gray;
    public clickOutsideToClose? = true;

    constructor(actionSheet?: ActionSheet) {
        if (typeof actionSheet.id !== 'undefined') {
            this.id = actionSheet.id;
        }
        if (typeof actionSheet.templateRef !== 'undefined') {
            this.templateRef = actionSheet.templateRef;
        }
        if (typeof actionSheet.context !== 'undefined') {
            this.context = actionSheet.context;
        }
        if (typeof actionSheet.height !== 'undefined') {
            this.height = actionSheet.height;
        }
        if (typeof actionSheet.showCloseButton !== 'undefined') {
            this.showCloseButton = actionSheet.showCloseButton;
        }
        if (typeof actionSheet.closeButtonColor !== 'undefined') {
            this.closeButtonColor = actionSheet.closeButtonColor;
        }
        if (typeof actionSheet.clickOutsideToClose !== 'undefined') {
            this.clickOutsideToClose = actionSheet.clickOutsideToClose;
        }
        if (typeof actionSheet.stickToTop !== 'undefined') {
            this.stickToTop = actionSheet.stickToTop;
        }
    }
}

export class Modal {
    public id: string;
    public title: string;
    public bodyComponent?: Type<any>;
    public bodyComponentRef?: ComponentRef<any>;
    public bodyText?: string;
    public bodyHtml?: string;
    public component?: Type<any>;
    public componentRef?: ComponentRef<any>;
    public templateRef?: TemplateRef<object>;
    public context?: object;
    public showHeader? = true;
    public showFooter? = false;
    public showOkButton? = false;
    public showCancelButton? = false;
    public showCloseButton? = false;
    public okButtonText? = ModalConstants.DefaultOkCopy;
    public okButtonSize? = ClassNames.Small;
    public cancelButtonText? = ModalConstants.DefaultCancelCopy;
    public onCancelClick?: () => any;
    public onBackdropClick?: () => any;
    public onCloseButtonClick?: () => any;
    public className?: string;
    public width? = ModalConstants.DefaultWidth;
    public onOkClick?: () => any;
    public clickOutsideToClose? = true;
    public isSubmitting$?: Observable<boolean>;
    public okButtonIsDisabled$?: Observable<boolean>;
    protected _data?: any;

    constructor(modal?: Partial<Modal>) {
        if (!!modal) {
            if (typeof modal.id !== 'undefined') {
                this.id = modal.id;
            }
            if (typeof modal.title !== 'undefined') {
                this.title = modal.title;
            }
            if (typeof modal.bodyComponent !== 'undefined') {
                this.bodyComponent = modal.bodyComponent;
            }
            if (typeof modal.bodyText !== 'undefined') {
                this.bodyText = modal.bodyText;
            }
            if (typeof modal.bodyHtml !== 'undefined') {
                this.bodyHtml = modal.bodyHtml;
            }
            if (typeof modal.component !== 'undefined') {
                this.component = modal.component;
            }
            if (typeof modal.templateRef !== 'undefined') {
                this.templateRef = modal.templateRef;
            }
            if (typeof modal.context !== 'undefined') {
                this.context = modal.context;
            }
            if (typeof modal.showHeader !== 'undefined') {
                this.showHeader = modal.showHeader;
            }
            if (typeof modal.showFooter !== 'undefined') {
                this.showFooter = modal.showFooter;
            }
            if (typeof modal.showOkButton !== 'undefined') {
                this.showOkButton = modal.showOkButton;
            }
            if (typeof modal.showCancelButton !== 'undefined') {
                this.showCancelButton = modal.showCancelButton;
            }
            if (typeof modal.showCloseButton !== 'undefined') {
                this.showCloseButton = modal.showCloseButton;
            }
            if (typeof modal.okButtonText !== 'undefined') {
                this.okButtonText = modal.okButtonText;
            }
            if (typeof modal.okButtonSize !== 'undefined') {
                this.okButtonSize = modal.okButtonSize;
            }
            if (typeof modal.cancelButtonText !== 'undefined') {
                this.cancelButtonText = modal.cancelButtonText;
            }
            if (typeof modal.className !== 'undefined') {
                this.className = modal.className;
            }
            if (typeof modal.width !== 'undefined') {
                this.width = modal.width;
            }
            if (typeof modal.onCancelClick !== 'undefined') {
                this.onCancelClick = modal.onCancelClick;
            }
            if (typeof modal.onBackdropClick !== 'undefined') {
                this.onBackdropClick = modal.onBackdropClick;
            }
            if (typeof modal.onCloseButtonClick !== 'undefined') {
                this.onCloseButtonClick = modal.onCloseButtonClick;
            }
            if (typeof modal.onOkClick !== 'undefined') {
                this.onOkClick = modal.onOkClick;
            }
            if (typeof modal.clickOutsideToClose !== 'undefined') {
                this.clickOutsideToClose = modal.clickOutsideToClose;
            }
            if (typeof modal.isSubmitting$ !== 'undefined') {
                this.isSubmitting$ = modal.isSubmitting$;
            }
            if (typeof modal.okButtonIsDisabled$ !== 'undefined') {
                this.okButtonIsDisabled$ = modal.okButtonIsDisabled$;
            }
            if (typeof modal.data !== 'undefined') {
                this._data = modal.data;
            }
        }
    }

    public get data(): any {
        return this._data;
    }
}

export interface SubscriptionLike {
    unsubscribe: () => void;
}

export class ZoneAwareStream<T> {
    private subject = new Subject<T>();
    private subscription: Subscription;
    private filterCondition: (filterArg: T) => boolean;

    constructor(
        private ngZone: NgZone,
        private sourceStream: Observable<any>,
        private runAlways = false
    ) {
        this.ngZone.runOutsideAngular(() => {
            this.subscription = this.sourceStream.subscribe((data) => {
                this.subject.next(data);
            });
        });
    }

    public filter(condition: (data: T) => boolean): ZoneAwareStream<T> {
        if (!this.filterCondition) {
            this.filterCondition = condition;
        }
        return this;
    }

    public subscribe(callback: (data: T) => void): SubscriptionLike {
        this.subject.subscribe((data) => {
            if (this.runAlways || (this.filterCondition && this.filterCondition(data))) {
                this.ngZone.run(() => callback(data));
            } else if (!this.filterCondition) {
                callback(data);
            }
        });
        return {
            unsubscribe: (): void => {
                this.subscription.unsubscribe();
                this.subject.unsubscribe();
            }
        };
    }
}

export interface ICardTab {
    title: string;
    active: boolean;
    templateRef?: TemplateRef<any>;
    component?: Type<any>;
}

export class SidebarMenuItemState {
    public showingDropdown = false;
    public showingAccordion = true;
}

export class SidebarProfile {
    public company: string;
    public accountExecutive?: string;
    public accountManager?: string;
    public bdo?: string;
    public underwriter?: string;

    constructor(item: SidebarProfile) {
        if (item) {
            this.company = item.company;
            this.accountExecutive = item.accountExecutive;
            this.accountManager = item.accountManager;
            this.bdo = item.bdo;
            this.underwriter = item.underwriter;
        }
    }
}

export class SidebarMenuItem {
    public label: string;
    public icon?: string;
    public routerLink?: string[];
    public modal?: Modal;
    public children?: SidebarMenuItem[] = [];
    public featureFlags?: string[];
    public claims?: number[];
    private _state? = new SidebarMenuItemState();

    constructor(item: SidebarMenuItem) {
        if (item) {
            this.label = item.label;
            this.icon = item.icon;
            this.routerLink = item.routerLink;
            this.modal = item.modal;
            this.children = item.children;
            this.featureFlags = item.featureFlags;
            this.claims = item.claims;
        }
    }

    public getState?(): SidebarMenuItemState {
        return this._state;
    }

    public toggleDropdown?(isDroppedDown?: boolean): void {
        this._state.showingDropdown =
            typeof isDroppedDown === 'undefined' ? !this._state.showingDropdown : isDroppedDown;
    }

    public toggleAccordion?(isShowing?: boolean): void {
        this._state.showingAccordion =
            typeof isShowing === 'undefined' ? !this._state.showingAccordion : isShowing;
    }
}

export interface IMobileBannerTab {
    label: string;
    templateRef?: TemplateRef<any>;
    isActive?: boolean;
}

export class MobileBannerTabsConfig {
    public tabs: IMobileBannerTab[] = [];
    public activeTab?: IMobileBannerTab;

    constructor(item: MobileBannerTabsConfig) {
        this.tabs = item.tabs && item.tabs.length > 0 ? item.tabs : [];
        this.activeTab = item.activeTab || null;
    }
}

export enum FilterStyle {
    Equal = 1,
    GreaterThanOrEqual = 2,
    LessThanOrEqual = 3,
    Between = 4
}

export enum FilterStyleDisplay {
    '=' = 1,
    '>=' = 2,
    '<=' = 3,
    Between = 4
}

export enum DateFilterStyle {
    Equal = 1,
    GreaterThan = 2,
    LessThan = 3,
    GreaterThanOrEqual = 4,
    LessThanOrEqual = 5
}

export enum DateFilterStyleDisplay {
    '=' = 1,
    '>' = 2,
    '<' = 3,
    '>=' = 4,
    '<=' = 5
}

export interface IOptionalExportServiceConfig {
    service: ApiService<any, any>;
    endpointName: string;
    label: string;
    dataSource: LsqApiServiceListSource<any, any>;
}

export interface ICloudinaryUploadResponse {
    bytes: number;
    created_at: string /* JSON-stringified Date */;
    etag: string;
    original_filename: string;
    placeholder: boolean;
    public_id: string;
    resource_type: string;
    secure_url: string;
    signature: string;
    tags: string[];
    type: 'upload' | 'private' | 'authenticated';
    url: string;
    version: number;
    pages?: number;
}

export interface ReportRequestUserSettings {
    take?: number;
}

export interface FloatingActionButton {
    identifier?: string;
    icon: string;
    action: (event: Event) => void;
    global?: boolean;
}

export interface TourStep {
    /** Selector for element that will be highlighted */
    selector?: string;
    /** Tour title text */
    title?: string;
    /** Tour step text */
    content: string;
    /** Where the tour step will appear next to the selected element */
    orientation?: Orientation;
    /** Action that happens when the step is opened */
    action?: () => void;
    /** Action that happens when the step is closed */
    closeAction?: () => void;
    /** Adds some padding for things like sticky headers when scrolling to an element */
    scrollAdjustment?: number;
    /** Adds default padding around tour highlighting */
    useHighlightPadding?: boolean;
}

export interface GuidedTour {
    tourId: string;
    useOrb?: boolean;
    steps: TourStep[];
}

export type Key = number | Guid;

export interface IBannerTabStep {
    title: string;
    routerLinkUrl?: string;
    routerLinkActiveOptions?: { exact: boolean };
    className?: string;
}

export enum FormType {
    Create = 1,
    Update = 2
}

export interface AutocompletePlace {
    companyName?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    phoneNumber?: string;
    website?: string;
    singleLineAddress?: string;
}

export class GenericCreateForgotPasswordRequest extends Request {
    public emailAddress: string = null;
    constructor(obj?: { emailAddress?: string }) {
        super();
        if (obj != null) {
            this.emailAddress =
                typeof obj.emailAddress === 'undefined' ? this.emailAddress : obj.emailAddress;
        }
    }
}

export interface IProtoAddress {
    street?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    zipCode?: string | null;
    name?: string | null;
    // TODO(dmayer): remove the keys with underscores once we figure out the improper camelCasing bug in protobuf-js.
    streetLine1?: string | null;
    streetLine2?: string | null;
    streetLine3?: string | null;
    streetLine4?: string | null;
    streetLine_1?: string | null;
    streetLine_2?: string | null;
    streetLine_3?: string | null;
    streetLine_4?: string | null;
    countryCode?: string | null;
}

export interface IGlobalSearch {
    [key: string]: string;
}

export class DoubleCallError extends Error {}

export interface TabsConfig {
    label: string;
    routerLink?: string;
    queryParamHandling?: string;
    queryParams?: object;
    icon?: string;
    iconPopoverText?: string;
    iconSize?: number;
    iconColor?: string;
    counter?: number;
    counterBgColor?: string;
    counterColor?: string;
}

export enum ButtonGroupType {
    First = 'button-group-first',
    Middle = 'button-group-middle',
    Last = 'button-group-last'
}
